import { App, createApp, defineAsyncComponent, ref } from "vue";
import { invokeWithLogin } from "@sazka/ng/utils/invoke-with-login";
import { httpClient } from "@sazka/utils/http-clients/http-client";
import { IdentityVerificationTexts } from "./identity-verification-texts";
import { useCookieStorage } from "@sazka/utils/use-storage";
import { UrlUtils } from "@sazka/utils/url";
import {
    handleBankAuthError,
    checkBankIdState,
    checkBusinessCardData,
    clearBankIdUrlParams,
} from "@/registration/composables/use-business-card";
import { RegistrationEcode } from "@/registration/utils/errors";
import { ngFrameworkLoaded } from "@sazka/ng/ng-integration";
import { loadCasinoModule } from "@sazka/casino/js/core/use-ng-casino";
import { Loader } from "@sazka/design-system/src/loader/loader";

export enum IdentityVerificationStep {
    VerificationChoice,
    ManualVerification,
    VerificationSuccessful,
    VerificationUnsuccessful,
}

const activeStep = ref(IdentityVerificationStep.VerificationChoice);
const error = ref<RegistrationEcode>(null);

let app: App<Element>;
let appWrapper: HTMLDivElement;

const showIdentityVerificationPopupInternal = () => {
    if (app) {
        return;
    }

    const identityVerificationPopup = defineAsyncComponent(() => import("./identity-verification-popup.vue"));
    app = createApp(identityVerificationPopup);

    if (!appWrapper) {
        appWrapper = document.createElement("div");
        document.body.appendChild(appWrapper);
    }

    Loader.showAboveAll();
    app.mount(appWrapper);
};

const checkBankIdRedirect = async () => {
    const storage = useCookieStorage();
    const cookie = storage.getItem("bank-id-state");

    if (!cookie || (!UrlUtils.getQueryString("code") && !UrlUtils.getQueryString("error"))) {
        return;
    }

    const errorFromRedirect = UrlUtils.getQueryString("error");
    if (errorFromRedirect) {
        error.value = handleBankAuthError(errorFromRedirect);
        activeStep.value = IdentityVerificationStep.VerificationUnsuccessful;
        showIdentityVerificationPopupInternal();
        return;
    }

    const codeFromRedirect = UrlUtils.getQueryString("code");
    if (codeFromRedirect) {
        if (!checkBankIdState()) {
            error.value = RegistrationEcode.InvokeBankIdFailed;
            activeStep.value = IdentityVerificationStep.VerificationUnsuccessful;
            showIdentityVerificationPopupInternal();
            return;
        }

        clearBankIdUrlParams();

        try {
            const isDataOk = await checkBusinessCardData(codeFromRedirect, true);
            if (isDataOk) {
                activeStep.value = IdentityVerificationStep.VerificationSuccessful;
            } else {
                error.value = RegistrationEcode.BankIdCheckIncomplete;
                activeStep.value = IdentityVerificationStep.VerificationUnsuccessful;
            }
        } catch {
            error.value = RegistrationEcode.BankIdCheckFailed;
            activeStep.value = IdentityVerificationStep.VerificationUnsuccessful;
        } finally {
            showIdentityVerificationPopupInternal();
        }
    }
};

const showIdentityVerificationPopup = () => invokeWithLogin(() => showIdentityVerificationPopupInternal());

const hideIdentityVerificationPopup = () => {
    app.unmount();
    app = null;
};

const fetchTexts = async () => {
    const response = await httpClient.get<IdentityVerificationTexts>("/api/identity-verification/texts");
    return response.data;
};

const init = async () => {
    await ngFrameworkLoaded();

    ng_hs.showPOSIDVerificationWidget(async () => {
        await showIdentityVerificationPopup();
        const casinoModule = (await loadCasinoModule()).useNgCasino();
        casinoModule?.closeGame();
    });
};

export const useIdentityVerificationPopup = () => {
    return {
        activeStep,
        error,
        checkBankIdRedirect,
        showIdentityVerificationPopup,
        hideIdentityVerificationPopup,
        fetchTexts,
        init,
    };
};
